/**
 * The horizontal progress bar for a guided creation flow.
 *
 * Sits along the bottom of the screen, separate from the tour's own vertical
 * stage tracker on the right: the two track different things at once — which
 * MODULE you are touring, and which STEP of the thing you are building — and
 * folding the second into the first would lose that distinction exactly when
 * both matter.
 *
 * Horizontal because a build is a sequence with an end, and a left-to-right run
 * of chips reads as "three more to go" at a glance. It also leaves the right-hand
 * side of the screen to the stage tracker rather than competing with it.
 */

import React from 'react';
import { createPortal } from 'react-dom';

import { useTourGuide } from '../TourGuideProvider';
import { placeTaskBar } from './barPlacement';
import { modalSafeChromeProps } from './modalSafe';

/**
 * What the bar says while a STEP is arriving, as opposed to the whole flow.
 *
 * Generic on purpose: `TaskFlow.preparingLabel` names the module being opened
 * ("Opening Automations…"), which is right for the first press and wrong three
 * steps later when what is loading is the builder canvas. Every flow would need
 * per-step copy to do better, and "the next screen" is true for all of them.
 */
const STEP_OPENING_LABEL = 'Opening the next screen…';

export function TaskFlowTracker() {
  const tour = useTourGuide();
  const listRef = React.useRef<HTMLOListElement | null>(null);
  const [viewport, setViewport] = React.useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));

  React.useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const task = tour?.task;
  const activeId = task?.step?.id ?? null;

  // Keep the active chip in view. A flow can be long enough to overflow on a
  // narrow screen, and the one chip the user needs to see is the current one.
  React.useEffect(() => {
    if (!activeId || !listRef.current) return;
    const chip = listRef.current.querySelector(`[data-step="${CSS.escape(activeId)}"]`);
    chip?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [activeId]);

  if (!task?.running) return null;

  /*
    Out of the dialog's way WITHOUT touching the dialog. The bar stays docked to
    the bottom edge and steps into the gutter beside whatever modal the flow has
    opened, narrowing to fit it; the host's own popup keeps exactly the
    dimensions it has with no walkthrough running. See `barPlacement.ts` for the
    stylesheet rule this replaced and what that rule did to the app's dialogs.
  */
  /*
    🔴 THE TARGET IS PASSED TOO, AND IT IS THE ONLY THING THAT CAN MOVE THE BAR
    NOW. On a step whose target sits in the bottom strip — the campaigns
    walkthrough's "Create and send", whose target is the drawer's own footer
    button — the pill was drawn over the very control the callout was telling
    the user to press. `placeTaskBar` lifts it clear; see `liftFor`.
  */
  const placement = placeTaskBar(task.avoid, viewport, task.target);

  /*
    🔴 THE BAR IS THE ONLY THING LEFT WHILE A STEP IS ARRIVING, so it has to say
    so. `TaskSpotlight` holds the callout back until the step's target exists
    (see `TaskRunState.step.awaiting`), and a run of chips with nothing else on
    screen reads as a walkthrough that stopped — the same reasoning that put the
    line here for `beforeStart` in the first place. `awaiting` is only ever true
    when the anchor genuinely did not resolve on entry, so an ordinary step
    never swaps the chips out for this.
  */
  const opening = task.preparing || Boolean(task.step?.awaiting);

  return createPortal(
    <aside
      className="tour-task-bar"
      data-testid="tour-task-bar"
      data-state={task.preparing ? 'preparing' : task.finished ? 'finished' : 'running'}
      data-dodge={placement.dodge}
      data-compact={placement.compact ? 'true' : 'false'}
      // Rendered so the lift is visible to a test and to anyone inspecting the
      // page, rather than buried in a computed length.
      data-lifted={placement.lift > 0 ? 'true' : 'false'}
      style={
        {
          '--tour-bar-inset': `${placement.inset}px`,
          '--tour-bar-max': `${placement.maxWidth}px`,
          '--tour-bar-lift': `${placement.lift}px`,
        } as React.CSSProperties
      }
      role="status"
      aria-live="polite"
      aria-label={
        task.preparing
          ? `${task.title}: ${task.preparingLabel}`
          : `${task.title}: step ${Math.min(task.index + 1, task.total)} of ${task.total}`
      }
      // Same reason as the callout — a modal dialog would otherwise dismiss
      // itself when the user presses the exit × or "Back to the tour".
      {...modalSafeChromeProps}
    >
      <div className="tour-task-bar__head">
        <span className="tour-task-bar__title">{task.title}</span>
        <span className="tour-task-bar__sub">
          {task.preparing
            ? 'Getting ready'
            : task.finished
              ? 'Finished'
              : `${Math.min(task.index + 1, task.total)} / ${task.total}`}
        </span>
      </div>

      {opening ? (
        /*
         * The gap between the Try-it press and the first step: a route change
         * plus a lazy-loaded page chunk, which on a cold cache is several
         * seconds. The chips would be honest here — every step really is
         * upcoming — but a run of grey numbers with nothing active reads as a
         * stalled flow rather than as one that is loading. One line that names
         * what is opening is the whole point.
         */
        <div className="tour-task-bar__preparing" data-testid="tour-task-preparing">
          <span className="tour-task-bar__spinner" aria-hidden="true" />
          <span className="tour-task-bar__preparingtext">
            {task.preparing ? task.preparingLabel : STEP_OPENING_LABEL}
          </span>
        </div>
      ) : task.finished ? (
        <div className="tour-task-bar__done" data-testid="tour-task-done">
          <div className="tour-task-bar__donetext">
            <strong>{task.doneTitle || task.title}</strong>
            {/*
              🔴 Reaching the end is NOT the same as having done the work. Skip is
              on every step, and the LAST step is the one that actually creates the
              record — so a run that skipped it finishes having built nothing.
              Saying "everything is done" there is a falsehood the user can check
              against their own list in about two seconds. This is why the
              skipped warning ALWAYS wins over `doneDescription`: a flow's own
              completion copy is written assuming success (e.g. "reopen it from
              the Draft filter") — showing that when the thing was never
              created would send the user hunting for something that isn't
              there, on top of the already-wrong impression that it worked.
            */}
            <span>
              {task.skippedCount > 0
                ? `Reached the end — ${task.skippedCount} step${task.skippedCount === 1 ? '' : 's'} skipped, so this may not have been created.`
                : task.doneDescription || 'Everything on the checklist is done.'}
            </span>
          </div>
          <button
            type="button"
            className="tour-task-bar__cta"
            // Finished, not just exited — carry the tour one step past this
            // stop rather than back onto the same popover. See the doc
            // comment on `TourGuideApi.endTask`.
            onClick={() => tour?.endTask('step')}
            data-testid="tour-task-resume"
          >
            Back to the tour
          </button>
        </div>
      ) : (
        <ol className="tour-task-bar__list" ref={listRef}>
          {task.steps.map((step, i) => (
            <li
              key={step.id}
              className="tour-task-bar__item"
              data-state={step.state}
              data-step={step.id}
              data-testid={`tour-task-step-${step.id}`}
              aria-current={step.state === 'active' ? 'step' : undefined}
            >
              <span className="tour-task-bar__marker" aria-hidden="true">
                {step.state === 'complete' ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : step.state === 'skipped' ? (
                  '–'
                ) : (
                  i + 1
                )}
              </span>
              <span className="tour-task-bar__label">{step.label}</span>
              {i < task.steps.length - 1 && (
                <span className="tour-task-bar__connector" aria-hidden="true" />
              )}
            </li>
          ))}
        </ol>
      )}

      {/*
        Leaving mid-flow puts the tour back where it was rather than dropping the
        user on a bare page — they were in the middle of a tour when they took
        this detour, and losing that thread is the worse outcome.
      */}
      {!task.finished && (
        <button
          type="button"
          className="tour-task-bar__close"
          onClick={() => tour?.endTask()}
          aria-label="Leave this walkthrough and go back to the tour"
          title="Leave this walkthrough"
          data-testid="tour-task-exit"
        >
          ×
        </button>
      )}
    </aside>,
    document.body,
  );
}
